/**
 * @module engine3d/models/downstream
 * @summary 3D models for Cable Downstream, Compounding Downstream and the
 *   Film & PP line — troughs, haul-offs, testers, winders and pelletisers.
 * @description
 *   Downstream kit is mostly long and low: open water troughs, roller banks and
 *   caterpillar tracks. Those read as 3D only if the *inside* is visible, so the
 *   troughs here are built as five separate walls with a water surface set into
 *   them rather than as a closed box.
 */
import {
  box,
  boxCentered,
  castors,
  cylinder,
  cylinderX,
  cylinderZ,
  dome,
  flange,
  frustum,
  gearbox,
  hmi,
  merge,
  motorUnit,
  nozzle,
  p,
  pm,
  rectFoot,
  revolve,
  ringLegs,
  rotateX,
  rotateY,
  rotateZ,
  roundFoot,
  screwFlight,
  filletPath,
  skid,
  translate,
  TAU,
  tube,
  v3,
  type Builder,
  type Mesh,
  type Model3,
} from './kit';

/**
 * The open-trough archetype: a legged tank with four walls, a floor, a liquid
 * surface and a line of spray headers. Cooling troughs, strand baths and
 * quenching baths differ only in proportion and fill level.
 */
function trough(fill: number, sprays: boolean, lidFrac: number): Builder {
  return ({ w, h, d }): Model3 => {
    const legH = h * 0.42;
    const bodyH = h - legH;
    const t = 1.5;
    const walls = merge(
      translate(box(w, bodyH, t), 0, legH, -(d / 2 - t / 2)),
      translate(box(w, bodyH, t), 0, legH, d / 2 - t / 2),
      translate(box(t, bodyH, d), w / 2 - t / 2, legH, 0),
      translate(box(t, bodyH, d), -(w / 2 - t / 2), legH, 0),
      translate(box(w, t, d), 0, legH, 0),
    );
    const legs: Mesh[] = [];
    for (const sx of [-1, 1])
      for (const sz of [-1, 1])
        legs.push(translate(boxCentered(3, legH, 3), sx * (w / 2 - 5), legH / 2, sz * (d / 2 - 4)));
    const spray: Mesh[] = [];
    if (sprays) {
      spray.push(translate(cylinderX(1.5, w * 0.9, 10), 0, legH + bodyH * 1.05, 0));
      for (let i = 0; i < 6; i++)
        spray.push(translate(cylinder(0.8, 3, { seg: 6 }), -w * 0.4 + (w * 0.8 * i) / 5, legH + bodyH * 0.95, 0));
    }
    return {
      parts: [
        p(merge(...legs), 'galv'),
        p(walls, 'stainless'),
        p(translate(box(w - t * 2, bodyH * fill, d - t * 2), 0, legH + t, 0), 'water'),
        ...(sprays ? [p(merge(...spray), 'pipe')] : []),
        ...(lidFrac > 0
          ? [p(translate(box(w * lidFrac, 1.4, d * 0.94), 0, legH + bodyH, 0), 'stainless')]
          : []),
        // Guide sheaves at each end, on the process line axis.
        p(
          merge(
            translate(cylinderZ(bodyH * 0.24, d * 0.14, 12), -w * 0.44, legH + bodyH * 0.72, 0),
            translate(cylinderZ(bodyH * 0.24, d * 0.14, 12), w * 0.44, legH + bodyH * 0.72, 0),
          ),
          'steel',
        ),
        p(nozzle(v3(0, legH + 2, -(d / 2)), v3(0, 0, -1), 2.2, 4), 'stainless'),
      ],
      height: legH + bodyH * 1.1 + (sprays ? 3 : 0),
      ports: {
        W: { p: v3(-w / 2, legH + bodyH * 0.72, 0), n: v3(-1, 0, 0) },
        E: { p: v3(w / 2, legH + bodyH * 0.72, 0), n: v3(1, 0, 0) },
        N: { p: v3(0, legH + bodyH * 1.1, 0), n: v3(0, 1, 0) },
        S: { p: v3(0, legH, d / 2), n: v3(0, 0, 1) },
      },
      footprint: rectFoot(w, d),
    };
  };
}

export const coolingtrough = trough(0.62, true, 0.3);
export const strandbath = trough(0.7, false, 0);
export const quenchbath = trough(0.66, true, 0.2);

/** Caterpillar haul-off — two opposed belt tracks on a heavy stand. */
export const cathaul: Builder = ({ w, h, d }): Model3 => {
  const standH = h * 0.4;
  const gap = h * 0.1;
  const trackH = h * 0.22;
  const cy = standH + gap / 2 + trackH;
  const len = w * 0.72;
  const track = (y: number): Mesh =>
    merge(
      translate(box(len, trackH, d * 0.5), 0, y, 0),
      translate(cylinderZ(trackH / 2, d * 0.52, 14), -len / 2, y + trackH / 2, 0),
      translate(cylinderZ(trackH / 2, d * 0.52, 14), len / 2, y + trackH / 2, 0),
    );
  return {
    parts: [
      p(skid(w * 0.96, d * 0.96, standH * 0.2), 'galv'),
      p(translate(box(w * 0.5, standH, d * 0.8), 0, standH * 0.2, 0), 'painted'),
      p(track(standH + gap / 2), 'rubber'),
      p(track(standH - gap / 2 - trackH), 'rubber'),
      // Pressure cylinders squeezing the upper track down.
      p(
        merge(
          translate(cylinder(h * 0.06, h * 0.2, { seg: 10 }), -len * 0.3, cy, d * 0.3),
          translate(cylinder(h * 0.06, h * 0.2, { seg: 10 }), len * 0.3, cy, d * 0.3),
        ),
        'accent',
      ),
      p(translate(merge(motorUnit(w * 0.24, h * 0.11).body, motorUnit(w * 0.24, h * 0.11).fins), w * 0.36, standH * 0.6, -d * 0.4), 'motor'),
      p(translate(gearbox(w * 0.12, h * 0.2, d * 0.3), w * 0.2, standH * 0.5, -d * 0.36), 'cast'),
      p(translate(hmi(w * 0.14, h * 0.3).arm, -w * 0.4, standH, d * 0.4), 'machine'),
      p(translate(hmi(w * 0.14, h * 0.3).screen, -w * 0.4, standH, d * 0.4), 'screen'),
    ],
    height: cy + h * 0.3,
    ports: {
      W: { p: v3(-w / 2, standH, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w / 2, standH, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + h * 0.3, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/**
 * The in-line instrument archetype: a small cabinet on a pedestal with the
 * process line running straight through it. Spark testers, gauges, monitors
 * and detectors are all this, with different faces.
 */
function inlineUnit(opts: { face: 'screen' | 'safety' | 'glass'; bore: number; dome?: boolean }): Builder {
  return ({ w, h, d }): Model3 => {
    const cy = h * 0.62;
    const bodyH = h * 0.42;
    return {
      parts: [
        p(translate(boxCentered(w * 0.22, cy - bodyH / 2, d * 0.32), 0, (cy - bodyH / 2) / 2, 0), 'machine'),
        p(translate(boxCentered(w * 0.86, bodyH, d * 0.8), 0, cy, 0), 'painted'),
        p(translate(box(w * 0.5, bodyH * 0.44, 1.2), 0, cy, d * 0.4), opts.face),
        ...(opts.dome ? [p(translate(dome(Math.min(w, d) * 0.2, 12), 0, cy + bodyH / 2, 0), 'safety')] : []),
        // The line passing through, with guide bushes either side.
        pm(
          'stainless',
          translate(cylinderX(w * opts.bore, w * 1.06, 10), 0, cy, 0),
          translate(cylinderX(w * opts.bore * 2.2, w * 0.06, 12), -w * 0.44, cy, 0),
          translate(cylinderX(w * opts.bore * 2.2, w * 0.06, 12), w * 0.44, cy, 0),
        ),
        p(translate(box(w * 0.3, bodyH * 0.18, d * 0.1), 0, cy + bodyH * 0.34, d * 0.4), 'screen'),
      ],
      height: cy + bodyH / 2 + (opts.dome ? Math.min(w, d) * 0.2 : 0),
      ports: {
        W: { p: v3(-w * 0.53, cy, 0), n: v3(-1, 0, 0) },
        E: { p: v3(w * 0.53, cy, 0), n: v3(1, 0, 0) },
        N: { p: v3(0, cy + bodyH / 2, 0), n: v3(0, 1, 0) },
        S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
      },
      footprint: rectFoot(w * 0.9, d),
    };
  };
}

export const sparktester = inlineUnit({ face: 'screen', bore: 0.028, dome: true });
export const odgauge = inlineUnit({ face: 'glass', bore: 0.03 });
export const eccmon = inlineUnit({ face: 'screen', bore: 0.03 });
export const metaldet = inlineUnit({ face: 'safety', bore: 0.05 });
export const preheater = inlineUnit({ face: 'safety', bore: 0.03 });
export const airknife = inlineUnit({ face: 'glass', bore: 0.05 });

/** Traversing winder — a driven drum on a stand with a traverse guide. */
export const winder: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, h) * 0.34;
  const cy = h * 0.56;
  const drumW = d * 0.62;
  return {
    parts: [
      pm(
        'machine',
        translate(boxCentered(w * 0.16, cy, d * 0.2), 0, cy / 2, drumW / 2 + d * 0.12),
        translate(boxCentered(w * 0.16, cy, d * 0.2), 0, cy / 2, -(drumW / 2 + d * 0.12)),
        translate(box(w * 0.9, h * 0.08, d * 0.9), 0, 0, 0),
      ),
      // Flanges + barrel of the drum, with the wound product on it.
      pm(
        'painted',
        translate(cylinderZ(R, 2.4, 18), 0, cy, drumW / 2),
        translate(cylinderZ(R, 2.4, 18), 0, cy, -drumW / 2 - 2.4),
        translate(cylinderZ(R * 0.36, drumW, 14), 0, cy, -drumW / 2),
      ),
      p(translate(cylinderZ(R * 0.82, drumW * 0.9, 18), 0, cy, -drumW * 0.45), 'cable'),
      p(translate(merge(motorUnit(w * 0.3, R * 0.24).body, motorUnit(w * 0.3, R * 0.24).fins), 0, cy * 0.5, -(drumW / 2 + d * 0.2)), 'motor'),
      // Traverse bar + flyer in front of the drum.
      pm(
        'steel',
        translate(cylinderZ(1.4, drumW * 1.1, 10), -R * 1.3, cy + R * 0.5, -drumW * 0.55),
        translate(boxCentered(3, 6, 3), -R * 1.3, cy + R * 0.5, 0),
      ),
      p(translate(hmi(w * 0.16, h * 0.3).arm, -w * 0.36, h * 0.08, d * 0.42), 'machine'),
      p(translate(hmi(w * 0.16, h * 0.3).screen, -w * 0.36, h * 0.08, d * 0.42), 'screen'),
    ],
    height: cy + R * 1.1,
    ports: {
      W: { p: v3(-R * 1.5, cy + R * 0.5, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w / 2, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + R * 1.1, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w * 0.9, d),
  };
};

/** Capstan / payoff — a large grooved wheel on a pedestal. */
export const capstan: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, h) * 0.4;
  const cy = h * 0.6;
  return {
    parts: [
      p(translate(boxCentered(w * 0.24, cy, d * 0.34), 0, cy / 2, -d * 0.2), 'machine'),
      p(
        merge(
          translate(cylinderZ(R, d * 0.2, 20), 0, cy, -d * 0.1),
          translate(cylinderZ(R * 1.08, 2, 20), 0, cy, d * 0.1),
          translate(cylinderZ(R * 1.08, 2, 20), 0, cy, -d * 0.12),
        ),
        'painted',
      ),
      p(translate(cylinderZ(R * 0.94, d * 0.16, 20), 0, cy, -d * 0.08), 'cable'),
      p(translate(merge(motorUnit(w * 0.34, R * 0.26).body, motorUnit(w * 0.34, R * 0.26).fins), 0, cy * 0.6, -d * 0.46), 'motor'),
      p(translate(box(w * 0.8, h * 0.08, d * 0.7), 0, 0, 0), 'galv'),
    ],
    height: cy + R * 1.1,
    ports: {
      W: { p: v3(-R * 1.1, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(R * 1.1, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, cy + R * 1.1, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w * 0.9, d),
  };
};

/** Accumulator — a tall tower of dancer sheaves on a slim frame. */
export const accumulator: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) * 0.3;
  const sheaves: Mesh[] = [];
  for (let i = 0; i < 4; i++) {
    const y = h * (0.3 + i * 0.2);
    sheaves.push(translate(cylinderZ(R, d * 0.2, 14), i % 2 ? w * 0.16 : -w * 0.16, y, -d * 0.1));
  }
  return {
    parts: [
      pm(
        'galv',
        translate(boxCentered(3, h, 3), w / 2 - 3, h / 2, d / 2 - 3),
        translate(boxCentered(3, h, 3), -(w / 2 - 3), h / 2, d / 2 - 3),
        translate(boxCentered(3, h, 3), w / 2 - 3, h / 2, -(d / 2 - 3)),
        translate(boxCentered(3, h, 3), -(w / 2 - 3), h / 2, -(d / 2 - 3)),
        translate(box(w, 2.4, d), 0, h, 0),
        translate(box(w, 2.4, d), 0, h * 0.02, 0),
      ),
      p(merge(...sheaves), 'painted'),
      p(translate(cylinder(1.6, h * 0.7, { seg: 8 }), 0, h * 0.2, 0), 'steel'),
      p(translate(box(w * 0.5, h * 0.1, 2), 0, h * 0.08, d / 2), 'panel'),
    ],
    height: h,
    ports: {
      W: { p: v3(-w / 2, h * 0.86, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w / 2, h * 0.86, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, h, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, h * 0.02, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Strand pelletiser — an infeed guide, cutting rotor and discharge chute. */
export const strandpellet: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, h) * 0.2;
  return {
    parts: [
      p(skid(w * 0.94, d * 0.94, h * 0.14), 'galv'),
      p(translate(box(w * 0.76, h * 0.5, d * 0.78), 0, h * 0.14, 0), 'painted'),
      // Hinged cutting-head cover, tilted open to show the rotor.
      p(translate(rotateZ(box(w * 0.5, 1.6, d * 0.74), -0.22), -w * 0.1, h * 0.64, 0), 'stainless'),
      p(translate(cylinderZ(R, d * 0.6, 16), 0, h * 0.56, -d * 0.3), 'steel'),
      p(translate(cylinderZ(R * 0.5, d * 0.62, 12), -R * 1.6, h * 0.56, -d * 0.31), 'rubber'),
      p(translate(merge(motorUnit(w * 0.3, R * 0.5).body, motorUnit(w * 0.3, R * 0.5).fins), w * 0.06, h * 0.32, -d * 0.6), 'motor'),
      // Discharge chute at the +X end.
      p(
        merge(
          translate(rotateZ(box(w * 0.24, 1.4, d * 0.5), 0.5), w * 0.42, h * 0.3, 0),
          translate(cylinder(Math.min(w, d) * 0.14, h * 0.12, { seg: 12 }), w * 0.4, h * 0.06, 0),
        ),
        'stainless',
      ),
      p(translate(box(w * 0.3, h * 0.3, 2), -w * 0.42, h * 0.14, d * 0.4), 'panel'),
    ],
    height: h * 0.78,
    ports: {
      W: { p: v3(-w / 2, h * 0.56, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.54, h * 0.3, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, h * 0.78, 0), n: v3(0, 1, 0) },
      S: { p: v3(w * 0.4, h * 0.06, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/**
 * The vertical process-column archetype: a legged vessel with a cone, a barrel
 * and a motor on top. Centrifugal dryers, classifiers, de-dusters and pellet
 * dryers are this with different tops and screen details.
 */
function verticalUnit(opts: {
  topMotor?: boolean;
  screen?: boolean;
  cap?: 'flat' | 'dish' | 'cone';
  coneFrac?: number;
}): Builder {
  return ({ w, h, d }): Model3 => {
    const R = Math.min(w, d) / 2;
    const legH = h * 0.14;
    const coneH = h * (opts.coneFrac ?? 0.28);
    const bodyH = h - legH - coneH - h * 0.14;
    const topY = legH + coneH + bodyH;
    const rings: Mesh[] = [];
    if (opts.screen)
      for (let i = 1; i < 4; i++)
        rings.push(translate(frustum(R * 1.03, R * 1.03, 2, { seg: 20 }), 0, legH + coneH + (bodyH * i) / 4, 0));
    return {
      parts: [
        p(ringLegs(R * 0.86, legH, 3, 1.7), 'galv'),
        p(
          revolve(
            [
              { x: R * 0.22, y: legH },
              { x: R, y: legH + coneH },
              { x: R, y: topY },
            ],
            22,
            false,
          ),
          'stainless',
        ),
        ...(opts.screen ? [p(merge(...rings), 'machine')] : []),
        p(
          opts.cap === 'dish'
            ? translate(dome(R, 20), 0, topY, 0)
            : opts.cap === 'cone'
              ? translate(frustum(R, R * 0.3, h * 0.14, { seg: 20 }), 0, topY, 0)
              : translate(cylinder(R * 1.03, h * 0.1, { seg: 20 }), 0, topY, 0),
          'painted',
        ),
        ...(opts.topMotor
          ? [
              p(translate(gearbox(R * 0.9, h * 0.1, R * 0.9), 0, topY + h * 0.14, 0), 'cast'),
              p(translate(cylinder(R * 0.5, h * 0.18, { seg: 14 }), 0, topY + h * 0.24, 0), 'motor'),
            ]
          : []),
        pm(
          'stainless',
          translate(cylinder(R * 0.22, legH * 0.6, { seg: 10 }), 0, legH * 0.2, 0),
          nozzle(v3(R * 0.98, legH + coneH + bodyH * 0.7, 0), v3(1, 0, 0), R * 0.16, 4),
          translate(cylinderZ(R * 0.2, d * 0.5, 10), -R * 0.4, legH + coneH + bodyH * 0.3, -d * 0.5),
        ),
        p(translate(box(R * 0.5, bodyH * 0.3, 1.4), 0, legH + coneH + bodyH * 0.3, R * 0.99), 'glass'),
      ],
      height: topY + h * 0.14 + (opts.topMotor ? h * 0.28 : 0),
      ports: {
        N: { p: v3(0, topY + h * 0.14 + (opts.topMotor ? h * 0.28 : 0), 0), n: v3(0, 1, 0) },
        S: { p: v3(0, legH * 0.2, 0), n: v3(0, -1, 0) },
        E: { p: v3(R * 1.04, legH + coneH + bodyH * 0.7, 0), n: v3(1, 0, 0) },
        W: { p: v3(-R, legH + coneH + bodyH * 0.4, 0), n: v3(-1, 0, 0) },
      },
      footprint: roundFoot(R * 1.06, 12),
    };
  };
}

export const centdryer = verticalUnit({ topMotor: true, screen: true, cap: 'flat' });
export const classifier = verticalUnit({ screen: true, cap: 'cone' });
export const dedusting = verticalUnit({ cap: 'dish', coneFrac: 0.34 });
export const pelletdryer = verticalUnit({ topMotor: true, cap: 'dish' });
export const waterringpellet = verticalUnit({ cap: 'dish', coneFrac: 0.3 });
export const fgsilo = verticalUnit({ cap: 'cone', coneFrac: 0.3 });

/**
 * Underwater pelletiser — the wheeled pedestal unit, modelled on the machine.
 *
 * Nothing about it is a vertical vessel: it is a painted base plate on castors,
 * a single square column, and a two-tier table at the top carrying the whole
 * cutting head lying along its own axis — water chamber and its handwheel at
 * one end, drive motor in the middle, bearing housing and die flange at the
 * other, with the process water pipework arching over the top.
 *
 * That arch is the thing you recognise the machine by, so it is drawn as real
 * swept pipe rather than suggested: `tube` along a filleted path, which is what
 * the elbows on a stainless water circuit actually look like.
 */
export const uwpellet: Builder = ({ w, h, d }): Model3 => {
  const wheelR = 2.6;
  const plateY = wheelR * 2;
  const plateT = h * 0.045;
  const lowY = h * 0.5;
  const shelfT = h * 0.03;
  const upY = h * 0.6;
  const deckTop = upY + shelfT;
  const R = Math.min(w, d) * 0.13; // cutting-head radius
  const cy = deckTop + R + 2; // machine centreline
  // Pipework clears the head but stays inside the height the 2D symbol claims:
  // `tallestOnLevel` spaces the floors off that number, so a machine that
  // overshoots its own symbol pushes everything above it up with it.
  const arch = cy + R * 1.9;
  const dieX = w * 0.34;
  const chamberX = -w * 0.24;

  // Process water: up off the housing, over the head, and down the far side.
  const waterRun = (dz: number, r: number): Mesh =>
    tube(
      filletPath(
        [
          v3(dieX * 0.72, cy + R * 0.8, dz),
          v3(dieX * 0.72, arch, dz),
          v3(chamberX * 0.9, arch, dz),
          v3(chamberX * 0.9, cy + R * 0.9, dz),
        ],
        R * 0.55,
        5,
      ),
      r,
      10,
    );

  return {
    parts: [
      p(castors(w * 0.66, d * 0.62, wheelR), 'rubber'),
      // Base plate, column and the two shelves — one painted weldment.
      pm(
        'painted',
        translate(box(w * 0.8, plateT, d * 0.74), 0, plateY, 0),
        translate(box(w * 0.19, lowY - plateY - plateT, d * 0.19), 0, plateY + plateT, 0),
        translate(box(w * 0.6, shelfT, d * 0.48), 0, lowY, 0),
        translate(box(w * 0.74, shelfT, d * 0.56), 0, upY, 0),
      ),
      // Height adjusters hanging off the top deck, and the terminal box.
      pm(
        'machine',
        ...[-0.3, 0.3].map((f) =>
          translate(cylinder(1.1, upY - lowY + shelfT * 2, { seg: 8 }), w * f, lowY - shelfT, d * 0.18),
        ),
        translate(box(w * 0.15, h * 0.14, d * 0.16), -w * 0.24, lowY - h * 0.15, 0),
        translate(box(w * 0.09, h * 0.07, d * 0.08), w * 0.13, lowY + shelfT, -d * 0.2),
      ),
      // ── Cutting head, lying along +X on the top deck ──
      // Water chamber, its domed cover and the handwheel that presses the
      // cutter onto the die face.
      p(translate(cylinderX(R, w * 0.28, 20), chamberX, cy, 0), 'paintedLight'),
      p(translate(rotateZ(dome(R * 0.92, 16), Math.PI / 2), chamberX - w * 0.14, cy, 0), 'hot'),
      pm(
        'rubber',
        translate(cylinderX(R * 0.66, 2.6, 14), chamberX - w * 0.2, cy, 0),
        translate(cylinderX(R * 0.16, w * 0.08, 10), chamberX - w * 0.21, cy, 0),
      ),
      // Drive motor.
      p(
        translate(
          merge(motorUnit(w * 0.22, R * 0.84).body, motorUnit(w * 0.22, R * 0.84).fins),
          w * 0.03,
          cy,
          0,
        ),
        'paintedLight',
      ),
      // Bearing housing and the die flange it bolts to the extruder through.
      pm(
        'machine',
        translate(cylinderX(R * 1.08, w * 0.13, 18), dieX * 0.62, cy, 0),
        translate(rotateZ(flange(R * 1.5, 3, 20), -Math.PI / 2), dieX, cy, 0),
        translate(rotateZ(flange(R * 1.2, 2.4, 20), -Math.PI / 2), dieX - w * 0.05, cy, 0),
      ),
      // Water pipework over the top: supply one side, return the other.
      pm('stainless', waterRun(-R * 0.5, R * 0.26), waterRun(R * 0.5, R * 0.2)),
      // Flexible hose down off the chamber to the pump below the deck.
      p(
        tube(
          filletPath(
            [
              v3(chamberX, cy - R, d * 0.1),
              v3(chamberX - w * 0.06, deckTop - h * 0.06, d * 0.14),
              v3(chamberX - w * 0.02, lowY + shelfT, d * 0.14),
            ],
            R * 0.5,
            4,
          ),
          R * 0.2,
          8,
        ),
        'lagging',
      ),
      // Warning plates: on the deck edge and on the base plate, as fitted.
      pm(
        'safety',
        translate(box(w * 0.11, 0.8, d * 0.05), -w * 0.2, upY + shelfT, d * 0.26),
        translate(box(w * 0.1, 0.8, d * 0.06), w * 0.14, plateY + plateT, d * 0.2),
      ),
    ],
    height: arch + R * 0.3,
    ports: {
      // Melt arrives through the die flange; pellets and water leave the
      // chamber; the water circuit connects over the top.
      E: { p: v3(dieX + 3, cy, 0), n: v3(1, 0, 0) },
      W: { p: v3(chamberX - w * 0.22, cy, 0), n: v3(-1, 0, 0) },
      N: { p: v3(chamberX * 0.9, arch + R * 0.26, -R * 0.5), n: v3(0, 1, 0) },
      S: { p: v3(0, plateY, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w * 0.82, d * 0.76),
  };
};

/** An open-ended shell lying along Z, centred on the origin. */
const shellZ = (r: number, len: number, seg = 24): Mesh =>
  translate(
    rotateX(frustum(r, r, len, { seg, capTop: false, capBottom: false }), -Math.PI / 2),
    0,
    0,
    len / 2,
  );

/**
 * Hot / dry-cut pelletiser — the cutter hood that bolts onto an extruder die.
 *
 * This is the machine the 2D symbol draws: a scroll hood enclosing a rotor that
 * shears the melt off a heated die plate, the pellets thrown outward by the
 * blades and dropping through a throat underneath. So it is built about the
 * CUTTER axis — along Z, die face at -Z presenting to the extruder behind it —
 * rather than as one more vertical vessel, and it stands on the wheeled trolley
 * these get rolled in and out of the line on.
 *
 * The front cover is hinged open, the same idiom `strandpellet` uses, because a
 * closed hood is an anonymous drum and the rotor is the whole point of it.
 */
export const hotcutpellet: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) * 0.32; // hood scroll radius
  const T = Math.min(w, d) * 0.34; // hood depth along the cutter axis
  const frameH = h * 0.07;
  const trolleyY = 5.2; // clear of the castors
  const standH = h * 0.32;
  const throatH = h * 0.15;
  const cy = standH + throatH + R; // cutter centreline
  const top = cy + R;

  // Six pitched blades on the rotor, sweeping the die face.
  const blades: Mesh[] = [];
  for (let i = 0; i < 6; i++) {
    const bl = boxCentered(R * 0.62, 1.6, T * 0.34);
    rotateX(bl, 0.34); // blade rake
    translate(bl, R * 0.45, 0, 0);
    rotateZ(bl, (TAU * i) / 6);
    blades.push(bl);
  }

  // Cutter drive, laid along the cutter axis in front of the hood.
  const mot = motorUnit(T * 0.8, R * 0.3);
  const drive = merge(mot.body, mot.fins);
  rotateY(drive, Math.PI / 2);
  translate(drive, 0, cy, T / 2 + T * 0.62);

  return {
    parts: [
      p(castors(w * 0.44, d * 0.5, 2.6), 'rubber'),
      p(translate(skid(w * 0.44, d * 0.5, frameH), 0, trolleyY, 0), 'galv'),
      // Column up to the hood, with a handle to push the trolley by.
      pm(
        'machine',
        translate(
          boxCentered(w * 0.2, standH - trolleyY - frameH, d * 0.24),
          0,
          (standH + trolleyY + frameH) / 2,
          0,
        ),
        translate(cylinderX(1.4, w * 0.34, 8), 0, standH * 0.86, d * 0.3),
      ),
      // Pellet throat: the scroll narrows into a chute and a flanged outlet.
      pm(
        'stainless',
        translate(frustum(R * 0.42, R * 0.68, throatH, { seg: 16 }), 0, standH, 0),
        translate(flange(R * 0.54, 2, 16), 0, standH - 2, 0),
      ),
      // Hood: an open-fronted scroll drum on the cutter axis.
      p(translate(shellZ(R, T, 26), 0, cy, 0), 'stainless'),
      // Back plate, and the adapter that lands on the extruder flange.
      pm(
        'machineDark',
        translate(cylinderZ(R * 1.04, 2.6, 26), 0, cy, -T / 2 - 1.3),
        translate(cylinderZ(R * 0.44, T * 0.4, 16), 0, cy, -T / 2 - T * 0.28),
        translate(cylinderZ(R * 0.62, 2.4, 18), 0, cy, -T / 2 - T * 0.5),
      ),
      // The die plate itself — heated, which is what makes it a HOT cut.
      p(translate(cylinderZ(R * 0.78, 3, 22), 0, cy, -T / 2 + 2.4), 'hot'),
      // Rotor: hub, blades, and the shaft through to the drive.
      pm(
        'steel',
        translate(cylinderZ(R * 0.3, T * 0.42, 16), 0, cy, T * 0.04),
        translate(merge(...blades), 0, cy, -T * 0.16),
        translate(cylinderZ(R * 0.11, T * 1.1, 12), 0, cy, T * 0.4),
      ),
      // Front: a bolted sight glass and its rim, so the rotor reads from outside.
      p(translate(cylinderZ(R * 0.96, 1.4, 26), 0, cy, T / 2 + 0.7), 'glass'),
      p(translate(shellZ(R * 1.03, 3.4, 26), 0, cy, T / 2 + 1.7), 'machine'),
      p(translate(gearbox(R * 0.62, R * 0.5, R * 0.62), 0, cy - R * 0.25, T / 2 + T * 0.26), 'cast'),
      p(drive, 'motor'),
      // Cooling-air blast into the hood, and the local control station.
      p(nozzle(v3(R * 0.72, cy + R * 0.6, 0), v3(0.6, 0.8, 0), R * 0.13, 6), 'pipe'),
      p(translate(box(w * 0.22, h * 0.16, 2), -w * 0.24, standH * 0.5, d * 0.26), 'panel'),
    ],
    height: top + R * 0.05,
    ports: {
      // The extruder bolts onto the die face behind; pellets fall out below.
      N: { p: v3(0, cy, -T / 2 - T * 0.52), n: v3(0, 0, -1) },
      S: { p: v3(0, standH - 2, 0), n: v3(0, -1, 0) },
      E: { p: v3(R * 1.02, cy, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R * 1.02, cy, 0), n: v3(-1, 0, 0) },
    },
    footprint: rectFoot(w * 0.52, d * 0.58),
  };
};

/**
 * Centrifugal dryer / dewatering unit, modelled on the machine itself: a
 * stainless column standing on a painted structural base frame, with a big
 * hinged inspection door down one side and the perforated screen basket
 * standing inside it. Wet pellets are tipped into a wide open hopper on the
 * right, water drains to the box under it, and the dried product leaves up the
 * duct on the roof beside the drive motor.
 *
 * The door is drawn OPEN, which is how these are always photographed and the
 * only way the basket — the part that does the work — is visible at all.
 */
export const centdewater: Builder = ({ w, h, d }): Model3 => {
  const baseH = h * 0.13; // painted structural base frame
  const colW = w * 0.46;
  const colD = d * 0.62;
  const colX = -w * 0.15; // column sits left of centre; the hopper takes the right
  const colY = baseH;
  const colTop = h * 0.8;
  const colH = colTop - colY;
  const drumR = colW * 0.36;
  const rimY = h * 0.5; // feed hopper lip
  const throatY = h * 0.34;
  const inletY = h * 0.29;
  const hopX = w * 0.24;
  const motorR = colW * 0.25;
  const motorH = h * 0.19;
  const top = colTop + 2.6 + motorH * 1.26 + 1;

  // ── The hinged door, built flat against the -X face and swung open ──
  const hingeX = colX - colW / 2 - 0.9;
  const hingeZ = colD / 2;
  const doorH = colH * 0.9;
  const doorW = colD * 0.95;
  /** Swing a door part about the vertical hinge on the column's front corner. */
  const swing = (m: Mesh): Mesh => {
    translate(m, 0, 0, doorW / 2); // hinge edge to the local origin
    rotateY(m, -1.85); // ~106 degrees open
    return translate(m, hingeX, colY + 3, hingeZ);
  };
  const doorSkin = swing(box(1.8, doorH, doorW));
  const doorFrame = swing(
    merge(
      translate(box(2.6, 3, doorW), 0, 0, 0),
      translate(box(2.6, 3, doorW), 0, doorH - 3, 0),
      translate(box(2.6, doorH, 3), 0, 0, -doorW / 2 + 1.5),
      translate(box(2.6, doorH, 3), 0, 0, doorW / 2 - 1.5),
      // Internal stiffener shelves, visible on the open leaf.
      translate(box(3.4, 2, doorW * 0.9), 0, doorH * 0.34, 0),
      translate(box(3.4, 2, doorW * 0.9), 0, doorH * 0.66, 0),
    ),
  );
  const doorMesh = swing(box(0.8, doorH * 0.82, doorW * 0.84));

  // ── Feed hopper: a square taper, so seg 4 turned 45 degrees ──
  const sq = (r: number, hh: number): Mesh =>
    rotateY(frustum(r, r, hh, { seg: 4 }), Math.PI / 4);
  const rimR = (w * 0.23) * Math.SQRT2;
  const throatR = (w * 0.065) * Math.SQRT2;
  const hopper = merge(
    translate(rotateY(frustum(throatR, rimR, rimY - throatY, { seg: 4 }), Math.PI / 4), 0, throatY, 0),
    translate(sq(rimR * 1.06, 2), 0, rimY - 2, 0),
  );

  return {
    parts: [
      // ── Painted structural base frame ──
      pm(
        'painted',
        ...[-1, 1].flatMap((sx) =>
          [-1, 1].map((sz) =>
            translate(
              boxCentered(4.4, baseH, 4.4),
              sx * (w * 0.44),
              baseH / 2,
              sz * (d * 0.4),
            ),
          ),
        ),
        translate(box(w * 0.94, 4.4, 4.4), 0, baseH - 4.4, d * 0.4),
        translate(box(w * 0.94, 4.4, 4.4), 0, baseH - 4.4, -d * 0.4),
        translate(box(4.4, 4.4, d * 0.86), w * 0.44, baseH - 4.4, 0),
        translate(box(4.4, 4.4, d * 0.86), -w * 0.44, baseH - 4.4, 0),
      ),
      // ── Stainless column, its roof, and the open door ──
      pm(
        'stainless',
        // Three walls: the -X side is the door opening.
        translate(box(colW, colH, 2), colX, colY, -(colD / 2 - 1)),
        translate(box(2, colH, colD), colX + colW / 2 - 1, colY, 0),
        translate(box(colW, colH, 2), colX, colY, colD / 2 - 1),
        translate(box(colW * 1.04, 2.6, colD * 1.04), colX, colTop, 0),
        translate(box(colW, 2.4, colD), colX, colY, 0),
        doorSkin,
      ),
      p(doorFrame, 'galv'),
      p(doorMesh, 'grating'),
      // ── Perforated screen basket, with its support rings and solid base ──
      p(
        translate(
          frustum(drumR, drumR, colH * 0.74, { seg: 20, capTop: false, capBottom: false }),
          colX,
          colY + colH * 0.2,
          0,
        ),
        'grating',
      ),
      pm(
        'stainless',
        ...[0.2, 0.42, 0.64, 0.86, 0.94].map((t) =>
          translate(frustum(drumR * 1.05, drumR * 1.05, 1.8, { seg: 20 }), colX, colY + colH * t, 0),
        ),
      ),
      // Slotted lower drum, and the rotor turning inside the basket.
      p(
        translate(
          frustum(drumR * 1.02, drumR * 1.02, colH * 0.2, { seg: 20, capBottom: false }),
          colX,
          colY + 2,
          0,
        ),
        'machine',
      ),
      p(
        translate(
          rotateZ(screwFlight(drumR * 0.74, colH * 0.7, 3, drumR * 0.2), Math.PI / 2),
          colX,
          colY + colH * 0.55,
          0,
        ),
        'stainless',
      ),
      // ── Drive on the roof: motor, terminal box, fan cowl ──
      pm(
        'motor',
        translate(cylinder(motorR, motorH, { seg: 16 }), colX + colW * 0.16, colTop + 2.6, 0),
        translate(
          boxCentered(motorR * 0.8, motorR * 0.7, motorR * 1.5),
          colX + colW * 0.16,
          colTop + motorH * 0.6,
          motorR,
        ),
        translate(
          frustum(motorR * 0.9, motorR * 0.62, motorH * 0.26, { seg: 14 }),
          colX + colW * 0.16,
          colTop + 2.6 + motorH,
          0,
        ),
      ),
      p(translate(box(colW * 0.8, 3, colD * 0.5), colX, colTop + 2.6, 0), 'galv'),
      // Dry-product duct, leaving the roof up and away over the door.
      p(
        translate(
          rotateZ(box(h * 0.2, 3.4, colD * 0.42), -0.72),
          colX - colW * 0.36,
          colTop + 3,
          0,
        ),
        'stainless',
      ),
      // ── Feed hopper on the right, and the pipe into the column ──
      p(translate(hopper, hopX, 0, 0), 'stainless'),
      pm(
        'stainless',
        translate(cylinder(throatR * 0.8, throatY - inletY, { seg: 14 }), hopX, inletY, 0),
        translate(cylinderX(w * 0.055, hopX - colX, 14), colX, inletY, 0),
      ),
      // The blue flexible coupling in the inlet run.
      p(translate(cylinderX(w * 0.062, w * 0.07, 14), hopX - w * 0.14, inletY, 0), 'painted'),
      // Water collection box under the hopper, and its drain through the frame.
      pm(
        'stainless',
        translate(box(w * 0.3, h * 0.11, d * 0.34), hopX + w * 0.04, baseH, d * 0.1),
        translate(cylinder(w * 0.05, baseH * 0.72, { seg: 12 }), hopX + w * 0.04, baseH * 0.28, d * 0.1),
      ),
      // ── Control station on the front of the column ──
      p(translate(box(w * 0.15, h * 0.13, 3), colX + colW * 0.2, rimY, colD / 2 + 1.4), 'machine'),
      pm(
        'panel',
        translate(box(w * 0.11, h * 0.09, 1), colX + colW * 0.2, rimY + h * 0.02, colD / 2 + 3),
      ),
    ],
    height: top,
    ports: {
      // Wet pellets are tipped into the hopper; dry product leaves the roof
      // duct; the water drains out of the bottom.
      N: { p: v3(colX - colW * 0.56, colTop + h * 0.1, 0), n: v3(0, 1, 0) },
      S: { p: v3(hopX + w * 0.04, baseH * 0.28, d * 0.1), n: v3(0, -1, 0) },
      E: { p: v3(hopX, rimY, 0), n: v3(0, 1, 0) },
      W: { p: v3(colX - colW / 2, colTop - 4, 0), n: v3(-1, 0, 0) },
    },
    // Wide enough to take the open door's shadow with it.
    footprint: [
      { x: -w * 0.98, y: -d * 0.46 },
      { x: w * 0.5, y: -d * 0.46 },
      { x: w * 0.5, y: d * 0.46 },
      { x: -w * 0.98, y: d * 0.46 },
    ],
  };
};

/** Bagging station — a weigh hopper over a spout with a pallet beneath. */
export const bagstation: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) * 0.34;
  const frameH = h * 0.9;
  return {
    parts: [
      pm(
        'galv',
        translate(boxCentered(3.2, frameH, 3.2), w / 2 - 3, frameH / 2, d / 2 - 3),
        translate(boxCentered(3.2, frameH, 3.2), -(w / 2 - 3), frameH / 2, d / 2 - 3),
        translate(boxCentered(3.2, frameH, 3.2), w / 2 - 3, frameH / 2, -(d / 2 - 3)),
        translate(boxCentered(3.2, frameH, 3.2), -(w / 2 - 3), frameH / 2, -(d / 2 - 3)),
        translate(box(w, 2.6, d), 0, frameH, 0),
      ),
      p(
        merge(
          translate(frustum(R * 0.3, R, h * 0.24, { seg: 18, capTop: false, capBottom: false }), 0, h * 0.5, 0),
          translate(cylinder(R, h * 0.16, { seg: 18, capBottom: false }), 0, h * 0.74, 0),
        ),
        'stainless',
      ),
      p(translate(cylinder(R * 0.28, h * 0.16, { seg: 12 }), 0, h * 0.34, 0), 'stainless'),
      // Load cells and the filled bag on its pallet.
      p(translate(cylinder(2.4, h * 0.04, { seg: 10 }), 0, h * 0.46, 0), 'accent'),
      p(translate(box(R * 1.5, h * 0.2, R * 1.1), 0, h * 0.08, 0), 'plastic'),
      p(translate(box(R * 2, h * 0.06, R * 1.6), 0, 0, 0), 'concrete'),
      p(translate(box(w * 0.24, h * 0.26, 2.2), -w * 0.34, h * 0.4, d / 2), 'panel'),
    ],
    height: frameH,
    ports: {
      N: { p: v3(0, frameH, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, h * 0.08, 0), n: v3(0, -1, 0) },
      E: { p: v3(w / 2, h * 0.62, 0), n: v3(1, 0, 0) },
      W: { p: v3(-w / 2, h * 0.62, 0), n: v3(-1, 0, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

/** Vibro screen — a stack of screen decks on springs with an unbalance drive. */
export const vibroscreen: Builder = ({ w, h, d }): Model3 => {
  const R = Math.min(w, d) / 2;
  const springH = h * 0.2;
  const deckH = h * 0.16;
  return {
    parts: [
      p(ringLegs(R * 0.9, springH * 0.6, 3, 1.6), 'galv'),
      p(
        merge(
          translate(cylinder(R * 0.14, springH * 0.5, { seg: 8 }), R * 0.8, springH * 0.5, 0),
          translate(cylinder(R * 0.14, springH * 0.5, { seg: 8 }), -R * 0.4, springH * 0.5, R * 0.7),
          translate(cylinder(R * 0.14, springH * 0.5, { seg: 8 }), -R * 0.4, springH * 0.5, -R * 0.7),
        ),
        'accent',
      ),
      pm(
        'stainless',
        translate(cylinder(R, deckH, { seg: 20 }), 0, springH, 0),
        translate(cylinder(R, deckH, { seg: 20, capBottom: false }), 0, springH + deckH * 1.1, 0),
        translate(frustum(R, R * 0.9, deckH * 0.7, { seg: 20 }), 0, springH + deckH * 2.2, 0),
      ),
      p(translate(cylinder(R * 0.42, h * 0.24, { seg: 14 }), 0, 0, 0), 'motor'),
      p(nozzle(v3(R * 0.98, springH + deckH * 0.5, 0), v3(1, 0, 0), R * 0.16, 4), 'stainless'),
      p(nozzle(v3(-R * 0.98, springH + deckH * 1.6, 0), v3(-1, 0, 0), R * 0.16, 4), 'stainless'),
    ],
    height: springH + deckH * 2.9,
    ports: {
      N: { p: v3(0, springH + deckH * 2.9, 0), n: v3(0, 1, 0) },
      E: { p: v3(R * 1.04, springH + deckH * 0.5, 0), n: v3(1, 0, 0) },
      W: { p: v3(-R * 1.04, springH + deckH * 1.6, 0), n: v3(-1, 0, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: roundFoot(R * 1.06, 12),
  };
};

/** Air cooling vibrator — a long vibrating pan on springs with a fan below. */
export const aircoolingvib: Builder = ({ w, h, d }): Model3 => {
  const panY = h * 0.5;
  return {
    parts: [
      pm(
        'galv',
        translate(boxCentered(4, panY * 0.7, 4), -w * 0.36, panY * 0.35, d / 2 - 4),
        translate(boxCentered(4, panY * 0.7, 4), w * 0.36, panY * 0.35, d / 2 - 4),
        translate(boxCentered(4, panY * 0.7, 4), -w * 0.36, panY * 0.35, -(d / 2 - 4)),
        translate(boxCentered(4, panY * 0.7, 4), w * 0.36, panY * 0.35, -(d / 2 - 4)),
      ),
      p(
        merge(
          translate(cylinder(2.6, panY * 0.3, { seg: 8 }), -w * 0.36, panY * 0.7, d / 2 - 4),
          translate(cylinder(2.6, panY * 0.3, { seg: 8 }), w * 0.36, panY * 0.7, d / 2 - 4),
          translate(cylinder(2.6, panY * 0.3, { seg: 8 }), -w * 0.36, panY * 0.7, -(d / 2 - 4)),
          translate(cylinder(2.6, panY * 0.3, { seg: 8 }), w * 0.36, panY * 0.7, -(d / 2 - 4)),
        ),
        'accent',
      ),
      // The pan: a floor with two raised sides, tilted very slightly.
      p(
        merge(
          translate(box(w * 0.94, 2, d * 0.86), 0, panY, 0),
          translate(box(w * 0.94, h * 0.16, 1.6), 0, panY, d * 0.43),
          translate(box(w * 0.94, h * 0.16, 1.6), 0, panY, -d * 0.43),
        ),
        'stainless',
      ),
      p(translate(box(w * 0.9, h * 0.02, d * 0.8), 0, panY + 2, 0), 'pellets'),
      p(translate(cylinderZ(h * 0.16, d * 0.3, 14), 0, panY * 0.4, -d * 0.15), 'motor'),
      p(translate(cylinder(h * 0.14, h * 0.12, { seg: 14 }), w * 0.2, panY * 0.1, 0), 'machine'),
    ],
    height: panY + h * 0.2,
    ports: {
      W: { p: v3(-w / 2, panY + h * 0.1, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w / 2, panY + h * 0.1, 0), n: v3(1, 0, 0) },
      N: { p: v3(0, panY + h * 0.2, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};

// ─── Film & PP line ──────────────────────────────────────────────────────

/** Roller stack — a driven bank of large chill / take-up rolls in a frame. */
function rollStack(n: number, heated: boolean): Builder {
  return ({ w, h, d }): Model3 => {
    const R = Math.min(h * 0.24, (w / n) * 0.38);
    const cy = h * 0.6;
    const rolls: Mesh[] = [];
    for (let i = 0; i < n; i++) {
      const x = -w * 0.36 + (w * 0.72 * i) / Math.max(1, n - 1);
      rolls.push(translate(cylinderZ(R, d * 0.78, 18), x, cy + (i % 2 ? R * 0.5 : -R * 0.5), -d * 0.39));
    }
    return {
      parts: [
        pm(
          'machine',
          translate(box(w * 0.98, cy, 3.4), 0, 0, d / 2 - 2),
          translate(box(w * 0.98, cy, 3.4), 0, 0, -(d / 2 - 2)),
          translate(box(w, h * 0.06, d), 0, 0, 0),
        ),
        p(merge(...rolls), heated ? 'steel' : 'painted'),
        p(
          merge(
            ...rolls.map((_, i) => {
              const x = -w * 0.36 + (w * 0.72 * i) / Math.max(1, n - 1);
              return translate(cylinderZ(R * 0.16, d * 0.94, 8), x, cy + (i % 2 ? R * 0.5 : -R * 0.5), -d * 0.47);
            }),
          ),
          'steel',
        ),
        p(translate(merge(motorUnit(w * 0.2, R * 0.4).body, motorUnit(w * 0.2, R * 0.4).fins), w * 0.3, cy * 0.4, -d * 0.62), 'motor'),
        p(translate(box(w * 0.22, h * 0.3, 2.2), -w * 0.36, cy * 0.3, d / 2), 'panel'),
      ],
      height: cy + R * 1.6,
      ports: {
        W: { p: v3(-w / 2, cy, 0), n: v3(-1, 0, 0) },
        E: { p: v3(w / 2, cy, 0), n: v3(1, 0, 0) },
        N: { p: v3(0, cy + R * 1.6, 0), n: v3(0, 1, 0) },
        S: { p: v3(0, 0, 0), n: v3(0, -1, 0) },
      },
      footprint: rectFoot(w, d),
    };
  };
}

export const pprollers = rollStack(4, false);
export const takeuprollers = rollStack(3, false);
export const takeupwinder = rollStack(2, false);

/** Stretching oven — an insulated tunnel with a viewing door and stack. */
export const stretchoven: Builder = ({ w, h, d }): Model3 => {
  const cy = h * 0.6;
  const bodyH = h * 0.62;
  return {
    parts: [
      pm(
        'galv',
        translate(boxCentered(4, h * 0.28, 4), -w * 0.4, h * 0.14, d / 2 - 4),
        translate(boxCentered(4, h * 0.28, 4), w * 0.4, h * 0.14, d / 2 - 4),
        translate(boxCentered(4, h * 0.28, 4), -w * 0.4, h * 0.14, -(d / 2 - 4)),
        translate(boxCentered(4, h * 0.28, 4), w * 0.4, h * 0.14, -(d / 2 - 4)),
      ),
      p(translate(box(w * 0.94, bodyH, d * 0.9), 0, h * 0.28, 0), 'lagging'),
      p(translate(box(w * 0.5, bodyH * 0.34, 1.6), 0, cy - bodyH * 0.1, d * 0.45), 'glass'),
      p(translate(box(w * 0.52, bodyH * 0.44, 2.4), 0, cy - bodyH * 0.16, d * 0.45), 'machine'),
      pm(
        'stainless',
        translate(cylinderX(Math.min(h, d) * 0.1, w * 1.06, 12), 0, cy, 0),
        translate(cylinder(Math.min(h, d) * 0.12, h * 0.2, { seg: 12 }), w * 0.28, h * 0.28 + bodyH, 0),
        translate(cylinder(Math.min(h, d) * 0.12, h * 0.2, { seg: 12 }), -w * 0.28, h * 0.28 + bodyH, 0),
      ),
      p(translate(box(w * 0.22, h * 0.3, 2.2), w * 0.38, h * 0.28, d / 2), 'panel'),
    ],
    height: h * 0.28 + bodyH + h * 0.2,
    ports: {
      W: { p: v3(-w * 0.53, cy, 0), n: v3(-1, 0, 0) },
      E: { p: v3(w * 0.53, cy, 0), n: v3(1, 0, 0) },
      N: { p: v3(w * 0.28, h * 0.28 + bodyH + h * 0.2, 0), n: v3(0, 1, 0) },
      S: { p: v3(0, h * 0.28, 0), n: v3(0, -1, 0) },
    },
    footprint: rectFoot(w, d),
  };
};
